import { Moment } from 'moment-timezone';
import { useTranslation } from 'react-i18next';
import { SxProp, View } from 'dripsy';
import ReactNative from 'react-native';
import React from 'react';

import { DateFormat } from '@/utils';
import { formatDate } from '@/utils/dateFormatting';
import { UniButton } from '../buttons';
import { ButtonVariant } from '../buttons/useButton.hook';
import { Divider } from '../Divider';
import { Icon } from '../icons/Icon';
import { TextBody, TextCaption } from '../typography';
import { Calendar } from './Calendar';
import { useDateRangePickerSelector } from './useDateRangePickerSelector';
import { DateRangeProps } from './useDateRangePicker.hook';

interface DateRangePickerSelectorProps {
  dateRangeProps: DateRangeProps;
  isOpen?: boolean;
  onValueChange?: (value: { from: Moment | null; to: Moment | null }) => void;
  onClose?: () => void;
  onConfirm?: () => void;
  onReset?: () => void;
}

export const DateRangePickerSelector: React.FC<DateRangePickerSelectorProps> = ({
  dateRangeProps,
  isOpen,
  onValueChange,
  onClose,
  onConfirm,
  onReset
}) => {
  const { t } = useTranslation('earn');
  const selector = useDateRangePickerSelector({ onValueChange });

  const dateRangePickerRef = React.useRef<ReactNative.View>(null);

  // if blur, close
  React.useEffect(() => {
    if (!isOpen) return;
    const close = () => {
      // if already selected, close
      if (dateRangeProps.from && dateRangeProps.to) {
        onClose?.();
        return;
      }
      // if not selected, reset
      selector.onReset();
      onClose && onClose();
    };
    document.addEventListener('click', close);
    // document.addEventListener('mousedown', close);
    return () => {
      document.removeEventListener('click', close);
      // document.addEventListener('mousedown', close);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;
  return (
    <View sx={styles.wrapper} ref={dateRangePickerRef}>
      <View sx={styles.container}>
        <Calendar
          side="LEFT"
          mode="DATE_RANGE"
          selectedDateRange={selector.selectedDateRange}
          selectedDate={null}
          calendar={selector.calendarLeft}
          onSelectDate={selector.onSelectDate}
          onNextMonth={selector.onNextMonth}
          onPreviousMonth={selector.onPreviousMonth}
          onNextYear={selector.onNextYear}
          onPreviousYear={selector.onPreviousYear}
        />
        <Calendar
          side="RIGHT"
          mode="DATE_RANGE"
          selectedDateRange={selector.selectedDateRange}
          selectedDate={null}
          calendar={selector.calendarRight}
          onSelectDate={selector.onSelectDate}
          onNextMonth={selector.onNextMonth}
          onPreviousMonth={selector.onPreviousMonth}
          onNextYear={selector.onNextYear}
          onPreviousYear={selector.onPreviousYear}
        />
      </View>
      <Divider sx={{ mt: '$3', mb: '$4' }} />
      <View sx={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', pb: '$1' }}>
        <View sx={{ flexDirection: 'row', alignItems: 'center', gap: '$3' }}>
          <TextCaption secondary>{t('earn-invest::glossary.date-selected')}</TextCaption>
          <TextBody>
            {selector.selectedDateRange.from
              ? formatDate(selector.selectedDateRange.from.toString(), { format: DateFormat.DATE_FORMAT.NO_TIME })
              : ' - '}
          </TextBody>
          <Icon name="arrowRight" />
          <TextBody>
            {selector.selectedDateRange.to
              ? formatDate(selector.selectedDateRange.to.toString(), { format: DateFormat.DATE_FORMAT.NO_TIME })
              : ' - '}
          </TextBody>
        </View>
        <View sx={{ flexDirection: 'row', gap: '$3', alignItems: 'center' }}>
          <UniButton
            label={t('earn-invest::glossary.reset')}
            size="small"
            variant={ButtonVariant.outline1}
            onPress={() => {
              onReset && onReset();
              selector.onReset();
            }}
            wrapperSx={{ width: 70 }}
          />
          <UniButton
            label={t('earn-invest::glossary.close')}
            size="small"
            variant={ButtonVariant.outline1}
            onPress={() => onClose && onClose()}
            wrapperSx={{ width: 70 }}
          />
          <UniButton
            label={t('earn-invest::glossary.done')}
            size="small"
            onPress={() => onConfirm && onConfirm()}
            wrapperSx={{ width: 70 }}
          />
        </View>
      </View>
    </View>
  );
};

enum StyleName {
  wrapper = 'wrapper',
  container = 'container',
  top = 'top',
  bottom = 'bottom'
}

const styles: Record<StyleName, SxProp> = {
  wrapper: {
    position: 'absolute',
    top: '$10',
    left: 0,
    padding: '$3',
    borderRadius: 5,
    backgroundColor: '$backgroundPopup',
    shadowColor: '#000000',
    shadowOffset: {
      width: 0,
      height: 2
    },
    shadowOpacity: 0.15,
    shadowRadius: 8,
    elevation: 5,
    zIndex: 100
  },
  container: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '$5'
  },
  top: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '$5'
  },
  bottom: {}
};
